import DynamicMap from '@ui/Map/Dynamic/component'
import ProductCard from '@ui/Product/Card/component'
import initSlider from '@ui/Slider/component'
import { createFetch, createNodeFromHTML, isMobile } from 'assets/core/js/common'
import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import type { MapConfig, MapMarker } from '@ui/Map/Dynamic/component'
import type { Slider } from '@ui/Slider/component'
import type { MapMouseEvent, Marker, Offset, Popup } from 'maplibre-gl'

// DEFAULT is used when the user doesn't want to see results after moving the map
// QUERY is used when the user wants to see results after moving the map
export const MAP_MODE = {
  DEFAULT: 'default',
  QUERY: 'query',
} as const

type MapModeKeys = keyof typeof MAP_MODE
type MapModeValuesTypes = (typeof MAP_MODE)[MapModeKeys]

type SearchMarker = Marker & {
  markerId?: string
}

export interface QueryResponse {
  markers: {
    id: string
    text: string
    coordinates: { lat: number; lon: number }
  }[]
  tiles: {
    [key: string]: string
  }
}

export interface SearchMapConfig extends MapConfig {
  mode?: MapModeValuesTypes
  queryUrl?: string | null
  onMove?(coordinates: number[][]): void
  onQuery?(response: QueryResponse): void
  onMarkerClick?(markerId: string): void
}

export default class SearchMap extends DynamicMap {
  activeMarker!: undefined | SearchMarker
  config!: SearchMapConfig
  queryAbortController: AbortController | null = null
  markerInstances: Map<string, SearchMarker> = new Map()
  slider: Slider | undefined
  sliderEl: HTMLElement | null = null
  loaderEl: HTMLElement | null = null
  refreshMapOnMove = true
  private markerProperties: {
    offset: Offset
    getOverlayText?(marker: MapMarker): string
    getContent?(markerId: string): HTMLElement | undefined
  } = {
    offset: [0, -55] as Offset,
  }

  constructor(element: string | HTMLElement, config: SearchMapConfig, markers: MapMarker[]) {
    super(element, config, markers)

    if (!this.config) {
      return
    }

    this.config.mode = config?.mode ?? MAP_MODE.DEFAULT

    if (this.config.mode === MAP_MODE.QUERY) {
      if (!this.element.hasAttribute('data-query-url')) {
        return
      }

      this.config.queryUrl = this.element.getAttribute('data-query-url')
    }

    this.activeMarker = undefined
  }

  async initMap(): Promise<void> {
    await super.initMap()

    this.toggleLoader('map', 'visible')

    if (this.config.mode === MAP_MODE.QUERY) {
      this.initSlider()
      this.initQueryMarkerProperties()
      this.initToggleQueryElement()
    }

    this.map.on('load', () => {
      this.isInitialized = true
      this.toggleLoader('map', 'hidden')
      this.config.onLoad?.()
    })

    // don't use the popup event as it will remove the active marker class
    this.map.on('click', (e: MapMouseEvent) => {
      const target = (e.originalEvent as MouseEvent).target as HTMLElement

      if (
        !target.classList.contains('maplibregl-marker') &&
        !target.classList.contains('marker-icon') &&
        !target.classList.contains('marker-overlay')
      ) {
        this.removeActiveMarker()
      }
    })

    // "moveend" is also triggered by "zoomend"
    this.map.on('moveend', (e) => {
      const coordinates = this.map.getBounds().toArray()
      // this event is also triggered when the map is loading but without the "originalEvent" property
      if (e.originalEvent) {
        if (this.config.mode === MAP_MODE.QUERY) {
          this.toggleLoader('move', 'visible')
          this.query(coordinates)
        }

        this.config.onMove?.(coordinates)
      }
    })

    if (this.config.mode === MAP_MODE.QUERY) {
      this.query(this.map.getBounds().toArray())
    }

    ElementPropertiesManager.addProperty(this.element, 'searchMap', this)
  }

  setMapMode(mode: SearchMapConfig['mode']): SearchMap {
    this.config.mode = mode

    return this
  }

  toggleLoader(type: 'map' | 'move', status: 'visible' | 'hidden'): void {
    this.loaderEl ??= document.getElementById('search-map-loader-block')
    const el = this.loaderEl

    if (!el || !this.refreshMapOnMove) {
      return
    }

    el.setAttribute('data-loader-type', type)

    if (status === 'hidden') {
      el.setAttribute('hidden', 'hidden')
    } else if (status === 'visible') {
      el.removeAttribute('hidden')
    }
  }

  initToggleQueryElement(): void {
    const el = document.getElementById('search-map-toggle-query')

    if (!el) {
      return
    }

    el.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement

      this.setMapMode(target.checked ? MAP_MODE.QUERY : MAP_MODE.DEFAULT)
      this.refreshMapOnMove = target.checked
    })
  }

  async query(coordinates: number[][]): Promise<void> {
    const separator = this.config.queryUrl?.includes('?') ? '&' : '?'
    const url = `${this.config.queryUrl}${separator}tr=${coordinates[1]?.join(',')}&bl=${coordinates[0]?.join(',')}`

    this.queryAbortController?.abort()
    this.queryAbortController = new AbortController()

    try {
      const response = await createFetch(url, {
        signal: this.queryAbortController.signal,
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        return
      }

      const data: QueryResponse = await response.json()

      this.updateSlider(data.tiles)

      this.toggleLoader('move', 'hidden')
      this.config.onQuery?.(data)

      const markers: MapMarker[] = Object.values(data.markers).map((marker) => ({
        id: marker.id.toString(),
        overlayText: marker.text,
        latitude: marker.coordinates.lat,
        longitude: marker.coordinates.lon,
      }))

      if (!this.isInitialized) {
        this.map.once('load', () => {
          this.setMarkers(markers)
        })
      } else {
        this.setMarkers(markers)
      }

      this.element.dispatchEvent(new CustomEvent('map-query-done'))
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }

      throw error
    }
  }

  setMarkers(markers: MapMarker[]): SearchMap {
    this.clearMarkers()
    for (const marker of markers) {
      this.setMarker(marker)
    }

    return this
  }

  clearMarkers(): SearchMap {
    for (const [id, marker] of this.markerInstances) {
      if (this.activeMarker && id === this.activeMarker.markerId) {
        continue
      }

      marker.remove()
      this.markerInstances.delete(id)
    }

    return this
  }

  setMarker(marker: MapMarker): SearchMarker | undefined {
    if (!marker.latitude || !marker.longitude) {
      return undefined
    }

    const markerId = (marker.id ?? '').toString()

    const overlayText = this.markerProperties.getOverlayText?.(marker) ?? marker.overlayText ?? null

    const overlay = overlayText ? `<div class="marker-overlay">${overlayText}</div>` : ''

    const markerTemplate = `<div class="marker-wrapper">
      <div class="marker-icon"></div>
      ${overlay}
    </div>`

    const markerEl = createNodeFromHTML(markerTemplate)
    markerEl.id = `marker-${markerId}`
    markerEl.addEventListener('mouseover', () => markerEl.classList.add('marker--hover'))
    markerEl.addEventListener('mouseout', () => markerEl.classList.remove('marker--hover'))
    markerEl.addEventListener('click', () => {
      this.removeActiveMarker()
      this.setActiveMarker(markerId)

      this.config.onMarkerClick?.(markerId)

      if (isMobile() && this.slider) {
        const slideEl = this.sliderEl?.querySelector(`.slide[data-id="${markerId}"]`)

        if (slideEl) {
          this.slider.slideTo(parseInt(slideEl.getAttribute('data-index') as string, 10))
        }
      }
    })

    const markerInstance: SearchMarker = new this.maplibregl.Marker({
      element: markerEl,
      anchor: 'bottom',
    }).setLngLat([marker.longitude, marker.latitude])

    if (this.markerProperties.getContent) {
      const popup: Popup & { _container?: HTMLElement } = new this.maplibregl.Popup({
        className: 'marker-popup',
        offset: this.markerProperties.offset,
      })
      const contentNode = this.markerProperties.getContent(markerId)
      if (contentNode) {
        popup.setDOMContent(contentNode)
      }
      popup.setMaxWidth('none')
      popup.on('open', () => {
        const pan: [number, number] = [0, 0]
        const point = this.map.project(popup.getLngLat())
        const popupRect = popup._container?.getBoundingClientRect()
        const mapRect = this.map.getContainer().getBoundingClientRect()

        if (!popupRect) {
          return
        }

        const popupRelativeLeft = popupRect.x - mapRect.x
        if (popupRelativeLeft < 0 || mapRect.width - popupRelativeLeft - (popup._container?.clientWidth ?? 0) < 0) {
          pan[0] = point.x - mapRect.width / 2
        }

        const popupRelativeTop = popupRect.y - mapRect.y
        if (popupRelativeTop < 0 || mapRect.height - popupRelativeTop - (popup._container?.clientHeight ?? 0) < 0) {
          pan[1] = point.y - (mapRect.height - popupRect.height) / 2
        }

        if (pan[0] === 0 && pan[1] === 0) {
          return
        }

        this.map.panBy(pan)
      })

      markerInstance.setPopup(popup)
    }

    markerInstance.addTo(this.map)
    markerInstance.markerId = markerId
    this.markerInstances.set(markerId, markerInstance)

    return markerInstance
  }

  openMarker(markerId: string): SearchMap {
    if (this.activeMarker?.getElement().id.replace('marker-', '') === markerId) {
      return this
    }

    if (this.activeMarker) {
      this.removeActiveMarker()
    }

    this.setActiveMarker(markerId)

    if (this.activeMarker?.getPopup() && !this.activeMarker?.getPopup().isOpen()) {
      this.activeMarker?.togglePopup()
    }

    this.config.onMarkerClick?.(markerId)

    return this
  }

  setActiveMarker(markerId: string): SearchMap {
    this.activeMarker = this.markerInstances.get(markerId)

    this.activeMarker?.getElement().classList.add('marker--active')

    return this
  }

  removeActiveMarker(): SearchMap {
    if (this.activeMarker) {
      const el = this.activeMarker.getElement()
      el.classList.remove('marker--active', 'marker--hover')

      if (this.activeMarker.getPopup()?.isOpen()) {
        this.activeMarker.togglePopup()
      }

      this.activeMarker = undefined
    }

    return this
  }

  getSliderElement(): HTMLElement | null {
    return this.sliderEl
  }

  private initQueryMarkerProperties(): void {
    this.markerProperties.getContent = (markerId) => {
      const sliderWrapper = this.sliderEl?.querySelector('.slider-wrapper')

      if (!sliderWrapper) {
        return
      }

      const cardEl = sliderWrapper.querySelector<HTMLElement>(`.product-card[data-id="${markerId}"]`)?.cloneNode(true) as HTMLElement

      if (!cardEl) {
        return
      }

      ProductCard(cardEl)

      return cardEl
    }
  }

  private initSlider(): void {
    this.sliderEl = this.element.parentElement?.querySelector<HTMLElement>('.map-products-slider') ?? null

    if (!this.sliderEl) {
      return
    }

    initSlider(this.sliderEl, {
      navigation: {
        nextEl: this.sliderEl.querySelector<HTMLElement>('.slider-button-next'),
        prevEl: this.sliderEl.querySelector<HTMLElement>('.slider-button-prev'),
      },
      spaceBetween: 10,
      lazy: false,
      speed: 1000,
      slidesPerView: 'auto',
      on: {
        init: (slider) => {
          this.slider = slider
        },
        slideChange: (slider) => {
          this.openMarker(slider.getSlides()[slider.getActiveIndex()]?.getAttribute('data-id') as string)
        },
        click: (slider) => {
          const url = slider.getSlides()[slider.getActiveIndex()]?.querySelector<HTMLLinkElement>('.product-card__link')?.href ?? null

          if (url) {
            window.location.href = url
          }
        },
      },
    })
  }

  private updateSlider(tiles: QueryResponse['tiles'], retries = 0): void {
    if (!this.slider) {
      if (retries < 20) {
        setTimeout(() => {
          this.updateSlider(tiles, retries + 1)
        }, 250)
      }

      return
    }

    const sliderWrapper = this.sliderEl?.querySelector('.slider-wrapper')

    if (!sliderWrapper) {
      return
    }

    sliderWrapper.innerHTML = ''

    Object.keys(tiles).forEach((tileId, i) => {
      const slideEl = document.createElement('div')
      slideEl.className = 'slide'
      slideEl.setAttribute('data-id', tileId)
      slideEl.setAttribute('data-index', i.toString())
      slideEl.innerHTML = tiles[tileId] as string

      sliderWrapper.appendChild(slideEl)
    })

    this.slider.update()
  }
}
